import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import TransactionTablePayBalanceInvoices from 'frontend-payments/components/transaction/table-pay-balance-invoices';
import type { Transaction } from 'frontend-payments/models';

import styles from './modal-pay-balance.module.css';

interface PaymentModalPayBalanceSignature {
  Args: {
    onCloseModal: () => void;
    transactions: Transaction[];
  };
}

export default class PaymentModalPayBalance extends Component<PaymentModalPayBalanceSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked rowSelection: CustomEmberTableRow[] = [];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalSelectedAmount() {
    if (!this.hasSelectedTransactions) {
      return 0;
    }
    return (
      this.intl.formatNumber(this.totalSelectedAmount, {
        style: 'currency',
        currency: this.selectedTransactions[0]?.currency,
      }) ?? this.intl.t('general.missing')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedTransactions() {
    return this.selectedTransactions.length > 0;
  }

  get selectedTransactions(): Transaction[] {
    return this.args.transactions.filter((transaction) =>
      this.rowSelection.some((row) => row['invoiceNumber'] === transaction.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalSelectedAmount() {
    return this.selectedTransactions.reduce((total, row) => {
      return total + (row.totalAmount ?? 0);
    }, 0);
  }

  @action
  onPaySelectedTransactions(): void {
    const transactionIds = this.selectedTransactions.map((t) => t.id);

    // Navigate to payment method selection with all transaction IDs as query params
    this.router.transitionTo('authenticated.payment-method', {
      queryParams: {
        transactionIds: transactionIds.join(','),
      },
    });

    this.args.onCloseModal();
  }

  @action
  setRowSelection(
    selection: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    this.rowSelection = Array.isArray(selection) ? selection : [selection];
  }

  <template>
    <PrismModal @onClose={{@onCloseModal}} @title={{t "payments.pay_balance"}}>
      <:default>
        <div class="modal--medium">
          <TransactionTablePayBalanceInvoices
            @isTransactionRequestLoading={{false}}
            @rowSelection={{this.rowSelection}}
            @setRowSelection={{this.setRowSelection}}
            @transactions={{@transactions}}
          />
        </div>
      </:default>
      <:footer>
        <div class={{concat styles.footer " " "flex justify-end"}}>
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onCloseModal}}
          />
          <PrismButton
            @isDisabled={{not this.hasSelectedTransactions}}
            @label={{t
              "payments.pay_balance.pay_now"
              amount=this.formattedTotalSelectedAmount
            }}
            @onClick={{this.onPaySelectedTransactions}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
